// Display-only table cells for the Kalshi fee-evidence and audit tables in My
// Trades. Moved verbatim out of index.tsx (#341). Each one is a pure function
// of its props: no fetching, no state, no store reads.
//
// These are NOT interchangeable with the cross-venue equivalents, and folding
// them together would be a visible change rather than a cleanup:
//
//  - Money/EstMoney render cents through formatDollars ("$1234.56"), while
//    tableKit's MoneyCell/PnlCell render dollars through formatMoney
//    ("$1,234.56"). Both are on screen at once, one venue tab apart.
//  - ActionBadge's palette differs from TradeCard's ACTION_STYLE on all four
//    actions — and amber means REJECTED here but OPEN there. Reconciling those
//    is a design decision on a trading surface, not a refactor.
//
// tradeCells.test.tsx pins both divergences so neither can be closed by
// accident.

import { maskPnl } from '../../lib/pnlMask';
import { formatDollars, formatDollars4 } from './tradeFormat';

// Action = lifecycle state of this contract group within the bucket. SELL
// and SETTLED are the two ways a position closes. OPEN is any portion still
// being held (only shown in the All view, hidden by the Closed-default
// filter). REJECTED is for the toggle view of orders that never filled.
export type ActionLabel = 'OPEN' | 'SELL' | 'SETTLED' | 'REJECTED';

export function Money({
  cents,
  muted,
  bold,
  precise,
  hide,
}: {
  cents: number | null;
  muted?: boolean;
  bold?: boolean;
  /** Render at 4 decimals for fee cells where Kalshi may have delivered
   *  sub-cent precision. Defaults false (2 decimals). */
  precise?: boolean;
  /** Mask the amount (P&L hidden). Renders `•••` regardless of value. */
  hide?: boolean;
}) {
  const cls = muted
    ? 'text-muted-foreground'
    : bold
      ? 'text-foreground font-semibold'
      : 'text-foreground';
  return (
    <td className={`whitespace-nowrap px-3 py-1.5 text-right font-mono ${cls}`}>
      {maskPnl(
        !!hide,
        cents == null
          ? '—'
          : precise
            ? formatDollars4(cents)
            : formatDollars(cents),
      )}
    </td>
  );
}

/** Estimate cell — displays the hydra fee estimate produced by
 *  estimateSideFee (aggregate per-fill ceil, apportioned to the row). 4-
 *  decimal precision because the apportioned value carries sub-cent
 *  fractions even though each portion is ceiled to the cent. */
export function EstMoney({
  singleFillCents,
  hide,
}: {
  singleFillCents: number | null;
  /** Mask the estimate (P&L hidden). Renders `•••` regardless of value. */
  hide?: boolean;
}) {
  if (singleFillCents == null) {
    return (
      <td className="text-muted-foreground whitespace-nowrap px-3 py-1.5 text-right font-mono">
        {maskPnl(!!hide, '—')}
      </td>
    );
  }
  return (
    <td className="text-foreground whitespace-nowrap px-3 py-1.5 text-right font-mono">
      {maskPnl(!!hide, formatDollars4(singleFillCents))}
    </td>
  );
}

export function ActionBadge({ action }: { action: ActionLabel }) {
  const cls =
    action === 'SELL'
      ? 'bg-purple-500/15 text-purple-400'
      : action === 'SETTLED'
        ? 'bg-blue-500/15 text-blue-400'
        : action === 'OPEN'
          ? 'bg-slate-500/15 text-slate-400'
          : 'bg-amber-500/15 text-amber-500';
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}
    >
      {action}
    </span>
  );
}
